import React, { useCallback } from 'react';
import ExportToS3Dialog from 'utils/ExportToS3Dialog';
import { useConfigInputs, useBom, useMetrics } from 'shared/state/configContexts';
import { useQuoteActionsCtx } from 'shared/state/quoteActionsContext';

/** Hosts the untouched ExportToS3Dialog, which auto-exports on mount once it has
 *  an opportunity id. It brings its own dialog chrome, so it is not wrapped in
 *  the shared Dialog; the restyle rides with the rest of the classic look in a
 *  later phase.
 *
 *  The escape gate is the classic one (App.js:1416): with exec overrides
 *  active, a csvExport escape must be logged before the file leaves; with none
 *  active the check short-circuits without logging.
 *
 *  No userName is passed any more: CONF-236 removed the Amplify-storage copy
 *  of the CSV, which was the only reader of the signed-in user's name. */
export function ExportDialog() {
  const { config, metadata } = useConfigInputs();
  const bom = useBom();
  const metrics = useMetrics();
  const { showExportDialog, setShowExportDialog, execEscapeActive, logEscape } = useQuoteActionsCtx();

  const onBeforeExport = useCallback(
    async (filename: string) => !execEscapeActive() || logEscape('csvExport', { quoteSerial: filename }),
    [execEscapeActive, logEscape],
  );

  return (
    <ExportToS3Dialog
      isOpen={showExportDialog}
      onClose={() => setShowExportDialog(false)}
      bom={bom}
      config={config}
      metrics={metrics}
      opportunityID={metadata.opportunityID}
      customerNumber={metadata.customerNumber}
      offerNumber={metadata.offerNumber}
      preparedFor={metadata.preparedFor}
      // Cast: ExportToS3Dialog.jsx is untyped JS whose default parameter
      // (`onBeforeExport = async () => true`) narrows tsc's inferred prop
      // type to a zero-arg callback, even though the component actually
      // invokes it with the filename (ExportToS3Dialog.jsx:169).
      onBeforeExport={onBeforeExport as unknown as () => Promise<boolean>}
    />
  );
}
